import React, { Component, Suspense, useEffect } from "react";
import { Route, BrowserRouter as Router, withRouter, Switch, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";

import createCookie from "./helpers/createCookie";
import "./index.scss";
import LoaderComponent from "./components/LoaderComponent";
import { trackPageView } from "./helpers/analytics";

// Components
const HeaderComponent = React.lazy(() => import("./components/HeaderComponent"));
const ScrollToTopComponent = React.lazy(() => import("./components/ScrollToTopComponent"));
const FooterComponent = React.lazy(() => import("./components/FooterComponent"));
const PromotionBarComponent = React.lazy(() => import("./components/PromotionBarComponent"));
const ExitModalComponent = React.lazy(() => import("./components/ExitModalComponent"));

// Pages
const HomePage = React.lazy(() => import("./pages/HomePage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const BookingPage = React.lazy(() => import("./pages/BookingPage"));
const DestinationsPage = React.lazy(() => import("./pages/DestinationsPage"));
const HelpCenterPage = React.lazy(() => import("./pages/HelpCenterPage"));
const BookingSummaryPage = React.lazy(() => import("./pages/BookingSummaryPage"));
const AirportTransfer = React.lazy(() => import("./pages/AirportTransfer"));
const PrivacyPolicyPage = React.lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsAndConditionsPage = React.lazy(() => import("./pages/TermsAndConditionsPage"));
const GroupTransferPage = React.lazy(() => import("./pages/GroupTransferPage"));
const BusRentalPage = React.lazy(() => import("./pages/BusRentalPage"));
const PremiumTransferPage = React.lazy(() => import("./pages/PremiumTransferPage"));
const PrivateBudapestChauffeurPage = React.lazy(() => import("./pages/PrivateBudapestChauffeurPage"));
const F1HungaroringTransferPage = React.lazy(() => import("./pages/F1HungaroringTransferPage"));
const CoronaVirusUpdatePage = React.lazy(() => import("./pages/CoronaVirusUpdatePage"));
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage"));

// Landing pages
const BudapestToViennaTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToViennaTransferPage"));
const BudapestToDebrecenTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToDebrecenTransferPage"));
const ViennaToBudapestTransferPage = React.lazy(() => import("./pages/landing-pages/ViennaToBudapestTransferPage"));
const BudapestToBratislavaTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToBratislavaTransferPage"));
const BudapestToHevizTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToHevizTransferPage"));
const BudapestToBalatonfuredTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToBalatonfuredTransferPage"));
const BudapestToSzantodTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToSzantodTransferPage"));
const BudapestToPragueTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToPragueTransferPage"));
const BudapestToZagrebTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToZagrebTransferPage"));
const BudapestToSplitTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToSplitTransferPage"));
const BudapestToDubrovnikTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToDubrovnikTransferPage"));
const BudapestToZadarTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToZadarTransferPage"));
const BudapestToSalzburgTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToSalzburgTransferPage"));
const BudapestToGrazTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToGrazTransferPage"));
const BudapestToCroatiaTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToCroatiaTransferPage"));
const BudapestToAustriaTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToAustriaTransferPage"));
// const BudapestToLjubljanaTransferPage = React.lazy(() => import("./pages/landing-pages/BudapestToLjubljanaTransferPage"));
const ExperienceItemsPage = React.lazy(() => import("./pages/landing-pages/Experiences/ExperienceItemsPage"));
const ExperiencesPage = React.lazy(() => import("./pages/landing-pages/Experiences"));

const HeaderComponentWithRoute = withRouter((props) => <HeaderComponent {...props} />);
const ScrollToTopComponentWithRoute = withRouter((props) => <ScrollToTopComponent {...props} />);
const ExitModalComponentWithRoute = withRouter((props) => <ExitModalComponent {...props} />);

// Create a wrapper component to handle page tracking
const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Extract page title from the current route, or use a default
    const pageTitle = document.title || "Pickup Hungary";
    
    // Track page view with current path and title
    trackPageView(location.pathname, pageTitle);
  }, [location]);

  return null;
};

export default class Routes extends Component {
    constructor(props) {
        super(props);
        this.state = {
            mouseOut: false,
            showExitIntent: false,
            lastActivity: Date.now()
        };

        this.setUtmCookie();
        this.setupExitIntentDetection();
    }
    
    setupExitIntentDetection() {
        // Mouse leave detection (user moving cursor to close tab/window)
        document.addEventListener('mouseleave', (e) => {
            // Only trigger if the mouse leaves from the top of the page
            if (e.clientY <= 0) {
                this.handleExitIntent();
            }
        });
        
        // Detect user inactivity (3 minutes)
        const inactivityTime = 3 * 60 * 1000; // 3 minutes in milliseconds
        
        // Reset the timer on user activity
        const resetTimer = () => {
            this.setState({ lastActivity: Date.now() });
        };
        
        // Check for inactivity every minute
        setInterval(() => {
            const now = Date.now();
            if (now - this.state.lastActivity > inactivityTime) {
                this.handleExitIntent();
            }
        }, 60000); // Check every minute
        
        // Reset timer on various user interactions
        ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
            document.addEventListener(event, resetTimer, true);
        });
    }
    
    handleExitIntent() {
        // Only show if we haven't shown it before in this session
        const exitPopupShown = sessionStorage.getItem('exitPopupShown');
        if (!exitPopupShown && !this.state.showExitIntent) {
            this.setState({ showExitIntent: true });
        }
    }

    setUtmCookie() {
        const urlParams = new URLSearchParams(window.location.search);

        const utm_source = urlParams.get("utm_source");
        const utm_medium = urlParams.get("utm_medium");
        const utm_campaign = urlParams.get("utm_campaign");
        const utm_term = urlParams.get("utm_term");
        const utm_content = urlParams.get("utm_content");

        const data = {
            utm_source: utm_source || "",
            utm_medium: utm_medium || "",
            utm_campaign: utm_campaign || "",
            utm_term: utm_term || "",
            utm_content: utm_content || "",
        };

        if (utm_source || utm_medium || utm_campaign || utm_term || utm_content) {
            createCookie("trackingData", JSON.stringify(data), 2);
        }
    }

    render() {
        return (
            <Router>
                <Helmet>
                    <title>Welcome to Pickup Hungary - Private transfer in Hungary</title>
                </Helmet>

                <PageTracker />

                <Suspense fallback={<LoaderComponent />}>
                    <ScrollToTopComponentWithRoute />
                    <div className="main" onMouseLeave={() => { if (!this.state.mouseOut) { this.setState({ mouseOut: true }); } }}>
                        {this.state.showExitIntent ? <ExitModalComponentWithRoute /> : null}
                        <PromotionBarComponent />
                        <HeaderComponentWithRoute />
                        <Switch>
                            <Route exact path="/" component={HomePage} />
                            <Route exact path="/about" component={AboutPage} />
                            <Route exact path="/booking/:step?" component={BookingPage} />
                            <Route exact path="/destinations" component={DestinationsPage} />
                            <Route exact path="/help-center" component={HelpCenterPage} />
                            <Route exact path="/bookingSummary/:bookingReference" component={BookingSummaryPage} />
                            <Route exact path="/budapest-airport-transfer" component={AirportTransfer} />
                            <Route exact path="/privacy-policy" component={PrivacyPolicyPage} />
                            <Route exact path="/terms-and-conditions" component={TermsAndConditionsPage} />
                            <Route exact path="/group-transfer-hungary" render={() => {
                                window.location.replace("/bus-rental-budapest");
                                return null;
                            }} />
                            <Route exact path="/bus-rental-budapest" component={BusRentalPage} />
                            <Route exact path="/private-budapest-chauffeur-service" component={PrivateBudapestChauffeurPage} />
                            <Route exact path="/premium-transfer-with-chauffeur" render={() => {
                                window.location.replace("/private-budapest-chauffeur-service");
                                return null;
                            }} />
                            <Route exact path="/premium-transfer-airport" render={() => {
                                window.location.replace("/private-budapest-chauffeur-service");
                                return null;
                            }} />
                            <Route exact path="/f1-hungaroring-transfer" component={F1HungaroringTransferPage} />
                            <Route exact path="/covid-19-safety-measures" component={CoronaVirusUpdatePage} />
                            {/* Landing pages */}
                            <Route exact path="/destinations/vienna-to-budapest-private-transfer" component={ViennaToBudapestTransferPage} />
                            <Route exact path="/destinations/budapest-to-vienna-private-transfer" component={BudapestToViennaTransferPage} />
                            <Route exact path="/destinations/budapest-to-debrecen-private-transfer" component={BudapestToDebrecenTransferPage} />
                            <Route exact path="/destinations/budapest-to-bratislava-private-transfer" component={BudapestToBratislavaTransferPage} />
                            <Route exact path="/destinations/budapest-to-heviz-private-transfer" component={BudapestToHevizTransferPage} />
                            <Route exact path="/destinations/budapest-to-balatonfured-private-transfer" component={BudapestToBalatonfuredTransferPage} />
                            <Route exact path="/destinations/budapest-to-szantod-private-transfer" component={BudapestToSzantodTransferPage} />
                            <Route exact path="/destinations/budapest-to-prague-private-transfer" component={BudapestToPragueTransferPage} />
                            <Route exact path="/destinations/budapest-to-zagreb-private-transfer" component={BudapestToZagrebTransferPage} />
                            <Route exact path="/destinations/budapest-to-split-private-transfer" component={BudapestToSplitTransferPage} />
                            <Route exact path="/destinations/budapest-to-dubrovnik-private-transfer" component={BudapestToDubrovnikTransferPage} />
                            <Route exact path="/destinations/budapest-to-zadar-private-transfer" component={BudapestToZadarTransferPage} />
                            <Route exact path="/destinations/budapest-to-croatia-private-transfer" component={BudapestToCroatiaTransferPage} />
                            <Route exact path="/destinations/budapest-to-salzburg-private-transfer" component={BudapestToSalzburgTransferPage} />
                            <Route exact path="/destinations/budapest-to-graz-private-transfer" component={BudapestToGrazTransferPage} />
                            <Route exact path="/destinations/budapest-to-austria-private-transfer" component={BudapestToAustriaTransferPage} />
                            {/* <Route exact path="/destinations/budapest-to-ljubljana-private-transfer" component={BudapestToLjubljanaTransferPage} /> */}
                            
                            {/* Experiences */}
                            <Route exact path="/experiences" component={ExperiencesPage} />
                            <Route path="/experiences/:experience" component={ExperienceItemsPage} />
                            
                            
                            <Route component={NotFoundPage} status={404} />
                        </Switch>
                        <FooterComponent />
                    </div>
                </Suspense>
            </Router>
        );
    }
}
